import { Card } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import { RiEditLine, RiSaveLine, RiArrowLeftLine } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";

export default function EditShopDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const shop = location.state?.shop;
  const shopId = shop?.id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    shopId: shopId || 0,
    shopName: shop?.shopName || "",
    shopCode: shop?.shopCode || "",
    basePrice: shop?.basePrice || 0,
  });

  // Check if shop data is available
  useEffect(() => {
    if (!shop || !shopId) {
      toast.error("Shop data is required");
      navigate("/my/auction");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "basePrice" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.shopName.trim()) {
      toast.error("Shop name is required");
      return;
    }
    if (!formData.shopCode.trim()) {
      toast.error("Shop code is required");
      return;
    }
    if (formData.basePrice <= 0) {
      toast.error("Base price must be greater than 0");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(`${server}/shop/updateBasicInfo`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
        },
      });

      toast.success(data?.message || "Shop details updated successfully!");
      setSubmitting(false);

      // Navigate back to auction page after successful update
      setTimeout(() => {
        navigate("/my/auction");
      }, 1000);
    } catch (e) {
      setSubmitting(false);
      toast.error(e?.response?.data?.message || e?.message || "Failed to update shop details");
    }
  };

  return (
    <div className="flex justify-center w-full p-4 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="w-full max-w-3xl">
        {/* Header Section */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/my/auction")}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors mb-4"
          >
            <RiArrowLeftLine className="text-xl" />
            <span className="font-medium">Back to Auction</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-3 rounded-xl shadow-lg">
              <RiEditLine className="text-3xl text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
                Edit Shop Details
              </h1>
              <p className="text-slate-500 mt-1">
                Update shop information and base price
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
          className="bg-white"
        >
          {loading ? (
            <div className="p-12 flex flex-col items-center justify-center">
              <svg className="animate-spin h-12 w-12 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-slate-500 font-medium">Loading shop details...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8">
              {/* Shop Name */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Shop Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="shopName"
                  value={formData.shopName}
                  onChange={handleInputChange}
                  placeholder="Enter shop name"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                  required
                />
              </div>

              {/* Shop Code */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Shop Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="shopCode"
                  value={formData.shopCode}
                  onChange={handleInputChange}
                  placeholder="Enter shop code"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                  required
                />
              </div>

              {/* Base Price */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Base Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    ₹
                  </span>
                  <input
                    type="number"
                    name="basePrice"
                    value={formData.basePrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-700 shadow-sm transition-all"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/my/auction")}
                  className="flex-1 py-3 px-6 rounded-lg font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 py-3 px-6 rounded-lg font-bold text-white shadow-lg transition-all duration-200 flex items-center justify-center gap-2 ${submitting
                      ? "bg-slate-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 hover:shadow-xl transform hover:-translate-y-0.5"
                    }`}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <RiSaveLine className="text-xl" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}